// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './SubstituteRegist.module.scss';
import { useEffect, useMemo, useState } from 'react';
import { createAxios } from 'ts/createAxios';
import { Form } from 'react-final-form';
import { ConfirmModal } from 'components/organisms/ConfirmModal';
import { validateInputEmissionNumber } from 'util/validator';
import {
  ImagePath,
  QUANTITY_INPUT_MAX_LENGTH,
  REGEX_OBJECT,
} from 'util/ConstantValues';
import { InputDataListField } from 'components/molecules/InputDataListField';
import { CategoryItem, ItemType, TareData, TareItem } from 'util/Types';
import { useTranslation } from 'react-i18next';
import { handleError } from 'util/errorHandler';
import { IconFixedItem } from 'components/atoms/icons/IconFixedItem';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router';
import { Path } from 'routes';
import { getUnitLabel, mergeClasses, normalizeNumber } from 'util/commons';
import { useWeightInput } from 'util/hooks/useScaleWeightInput';
import { ScaleType } from 'util/Enums';
import { useQuery } from 'util/queryParams';
import {
  CONNECT_SCALE_STORAGE_KEY,
  ScaleConnectionStatus,
} from 'util/hooks/useConnectToScale';
import NumberWheel from 'components/molecules/NumberWheel';

type Props = {
  emissionType: CategoryItem;
  // 代理登録を行う対象となる排出拠点ID
  substituteRegistFromSiteId: string;
  afterSubmit: () => void;
  scalesType?: ScaleType;
};

type Input = {
  quantity?: string;
};

export function SubstituteRegistInput({
  emissionType,
  afterSubmit,
  substituteRegistFromSiteId,
  scalesType,
}: Props) {
  const api = createAxios();
  const history = useHistory();
  const tenantId = useQuery().get('tenantId');
  const customType = emissionType.type || ItemType.OPTIONAL;
  const prevUrl = useQuery().get('prevUrl') || Path.collects;
  const [tare, setTares] = useState<TareData>();
  const [isConfirm, setIsConfirm] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Partial<TareItem>>();
  const { t } = useTranslation();
  const initInputData: Input = {
    quantity: '',
  };

  useEffect(() => {
    if (!scalesType) {
      return;
    }

    const handleSetScaleUsing = (isUsing: boolean) => {
      try {
        window?.setScaleUsing?.(isUsing);
        window?.connectWithScale?.(true, scalesType);
        localStorage.setItem(
          CONNECT_SCALE_STORAGE_KEY,
          ScaleConnectionStatus.ON
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (!e?.message?.includes?.('Android') && e.message) {
          toast.error(e.message);
        }
      }
    };

    handleSetScaleUsing(true);

    return () => {
      handleSetScaleUsing(false);
    };
  }, [scalesType]);

  const { weightInputRef } = useWeightInput({
    receiveValueFromScaleCondition: !isConfirm,
    itemType: customType,
  });

  const fetchTares = () => {
    api
      .get<TareData>(`/tares?siteId=${substituteRegistFromSiteId}`)
      .then((res) => {
        setTares(res.data);
      })
      .catch((e) => {
        handleError(e);
      });
  };

  const handleSelected = (tareItem: TareItem) => {
    setSelectedItem((prev) => {
      if (prev?.id === tareItem.id) {
        return { ...prev, id: undefined, weight: '0' };
      }

      return tareItem;
    });
  };

  useEffect(() => {
    fetchTares();
  }, []);

  const convertedTares = useMemo(
    () =>
      [...(tare?.tares ?? [])]
        .filter(
          (item) =>
            !tenantId ||
            !item.tenants?.length ||
            item.tenants.some((e) => e.id === Number(tenantId))
        )
        .sort(({ tenants }) => (tenants?.length ? -1 : 1)),
    [tare, tenantId]
  );

  const getConfirmQuantity = (quantity?: string) => {
    if (customType === ItemType.OPTIONAL && selectedItem) {
      const subQuantity = Number(quantity) - Number(selectedItem.weight);

      return subQuantity < 0
        ? 0
        : normalizeNumber({
            value: subQuantity,
            commaSplit: false,
          });
    }

    return quantity;
  };

  const submitSubstituteRegist = (
    values: Input,
    isSubmitAndFinish?: boolean
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const waste = [] as any[];
    if (!isConfirm) return;
    if (values.quantity && values.quantity != '') {
      const quantity = getConfirmQuantity(values.quantity);
      if (isNaN(Number(quantity))) {
        toast.error(t('emission_input.input_number'));
      }
      waste.push({
        type: customType,
        wasteUnitId: Number(emissionType.id),
        quantity: Number(quantity),
        ...(tenantId ? { tenantId: Number(tenantId) } : {}),
        ...(customType === ItemType.OPTIONAL
          ? {
              tareName: selectedItem?.name,
              tareWeight: selectedItem?.weight,
            }
          : {}),
      });
    }
    if (waste.length === 0) {
      return;
    }
    api
      .post('/collects/substitute-regists', {
        siteId: substituteRegistFromSiteId,
        waste: waste,
      })

      .then(() => {
        setIsConfirm(false);
        if (!isSubmitAndFinish) {
          afterSubmit();
        } else {
          const prevUrlObj = new URL(`${window.location.origin}${prevUrl}`);
          prevUrlObj.searchParams.delete('tenantId');

          history.push(`${prevUrlObj.pathname}${prevUrlObj.search}`);
        }
        toast.success(t('messages.M_109'));
        // FIXME: Android 向け機能は一旦コメントアウト
        // if (window && window.postEmissions) {
        //   window.postEmissions(
        //     JSON.stringify([
        //       {
        //         ...res.data[0],
        //         emissionDatetime: getFormatDateWithCurrentTimezone(
        //           res.data[0].emissionDatetime,
        //           DATE_FORMAT.slaYMDHms
        //         ),
        //       },
        //     ]),
        //     i18n.language
        //   );
        // }
      })
      .catch((e) => {
        handleError(e);
      });
  };

  return (
    <div className={styles.emissionInput}>
      <Form<Input>
        initialValue={initInputData}
        validate={(values) => {
          return {
            quantity: validateInputEmissionNumber(
              values.quantity || '',
              customType
            ),
          };
        }}
        onSubmit={() => {}}
      >
        {({ handleSubmit, valid, values }) => {
          return (
            <form
              onSubmit={handleSubmit}
              className={mergeClasses(styles.formInput, {
                [styles.formInputWithTare]: customType === ItemType.OPTIONAL,
              })}
            >
              <div>
                <div className={styles.inputType}>
                  <div className={styles.emissionBox}>
                    <img
                      className={styles.emissionImage}
                      src={
                        emissionType.imageUrl
                          ? `${process.env.REACT_APP_CLOUDFRONT}${emissionType.imageUrl}`
                          : ImagePath.defaultImage
                      }
                    />
                  </div>

                  <div className={styles.commonInfo}>
                    <div className={styles.info}>
                      <div className={styles.labelBox}>
                        <div className={styles.label}>{emissionType.name}</div>
                        {customType === ItemType.FIX && (
                          <div className={styles.type}>
                            <div className={`${styles.itemType}`}>
                              <IconFixedItem />
                            </div>
                          </div>
                        )}
                      </div>

                      {customType === ItemType.FIX && (
                        <div className={styles.weightFix}>{`${
                          emissionType.convertKg
                        }kg/${emissionType.quantity}${getUnitLabel(
                          emissionType.unit
                        )}`}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className={styles.weight}>
                  <div className={styles.weightField}>
                    <InputDataListField
                      placeholder="0"
                      inputRef={weightInputRef}
                      name="quantity"
                      required
                      type="text"
                      maxlength={QUANTITY_INPUT_MAX_LENGTH}
                      validRegex={
                        customType === ItemType.FIX
                          ? REGEX_OBJECT.number
                          : REGEX_OBJECT.fiveDigitsWith2Decimal
                      }
                    />
                    {customType !== ItemType.FIX && (
                      <span className={styles.weightUnit}>kg</span>
                    )}
                  </div>
                </div>

                <button
                  className={`${styles.submitButton} ${
                    !valid ? styles.disableButton : ''
                  }`}
                  disabled={!valid}
                  type="button"
                  onClick={() => {
                    setIsConfirm(true);
                    return false;
                  }}
                >
                  {t('emission_input.button_submit')}
                </button>

                {emissionType.type === ItemType.OPTIONAL && (
                  <div className={styles.emissionTareBox}>
                    {convertedTares.length > 0 && (
                      <div className={styles.emissionTare}>
                        {convertedTares.map((item) => (
                          <span
                            key={item.id}
                            className={mergeClasses({
                              [styles.emissionSelected]:
                                selectedItem?.id === item.id,
                            })}
                            onClick={() => {
                              handleSelected(item);
                            }}
                          >
                            {`${
                              item.name.length > 20
                                ? `${item.name.slice(
                                    0,
                                    18 - item.weight.length
                                  )}...`
                                : item.name
                            } (${normalizeNumber({
                              value: item.weight,
                            })}kg)`}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className={styles.tareInputBox}>
                      <div className={styles.tareInputTitle}>
                        {t('emission_input.tare_wheel_title')}
                      </div>
                      <div className={styles.tareInput}>
                        <NumberWheel
                          value={Number(selectedItem?.weight || '0')}
                          separator="."
                          onChange={(value) =>
                            setSelectedItem({ weight: String(value) })
                          }
                        />
                        <div className={styles.tareInputUnit}>kg</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <ConfirmModal
                fullBottomWidth
                title={t('emission_input.confirm_title')}
                submitLabel={
                  t('substitute_regist_new.collect_and_finish') ||
                  '代理登録して回収する'
                }
                isOpen={isConfirm}
                emissionType={emissionType}
                closeEditModal={() => {
                  setIsConfirm(false);
                }}
                onClick={() => {
                  submitSubstituteRegist(values, true);
                }}
                confirmContents={[
                  ...(selectedItem
                    ? [
                        {
                          label: `${
                            selectedItem.name ||
                            t('emission_input.tare_wheel_title')
                          }:`,
                          contents: `${normalizeNumber({
                            value: selectedItem.weight,
                          })}kg`,
                        },
                      ]
                    : []),
                  customType === ItemType.FIX
                    ? {
                        label: t('emission_input.label_item_name'),
                        contents: `${emissionType.name} ${
                          emissionType.convertKg
                        }kg/${emissionType.quantity}${getUnitLabel(
                          emissionType.unit
                        )}`,
                      }
                    : {
                        label: t('emission_input.label_item_name'),
                        contents: emissionType.name,
                      },
                  {
                    label:
                      customType == ItemType.OPTIONAL
                        ? t('emission_input.label_weight')
                        : t('emission_input.label_content'),
                    contents: `${getConfirmQuantity(values.quantity)} ${
                      customType == ItemType.FIX && emissionType.inputUnit
                        ? emissionType.inputUnit
                        : customType == ItemType.OPTIONAL
                        ? 'kg'
                        : ''
                    }`,
                  },
                  customType === ItemType.FIX
                    ? {
                        label: t('emission_input.label_weight'),
                        contents: `${
                          Number(emissionType.convertKg) *
                          Number(values.quantity)
                        } kg`,
                      }
                    : { label: '', contents: '' },
                ]}
              />
            </form>
          );
        }}
      </Form>
    </div>
  );
}
